import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { select, Store } from '@ngrx/store';

import { TaskMetaInformation } from '@modeso/aumico-lib-cpmm-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { I18NextService } from 'angular-i18next';
import { Observable } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import { clearScrollToText, setAccordionOpenState, setSelectedTabState } from '../../actions/ui.actions';
import { CreateTaskRequestDto } from '../../dtos/create-task-dto.interface';
import { DocumentDto } from '../../dtos/document-dto.interface';
import { UpdateTaskDto } from '../../dtos/update-task-dto.interface';
import { ExportTasksFilterState } from '../../enums/export-tasks-filter.enum';
import { TaskState } from '../../enums/task-state.enum';
import { WorkflowArea } from '../../enums/workflow-area.enum';
import { selectAccordionOpenState, selectScrollToTextState, selectSelectedTabState } from '../../reducers/ui.reducer';
import { GetUserDto } from '@modeso/aumico-lib-users-fe';
/**
 * A generic task structure. The component expects tasks to have these optional properties.
 */
export interface Task {
    taskType?: TaskType | string;
    taskState?: TaskState;
    dueDate?: Date;
    sectionName?: string;
    [key: string]: any; // Allow other properties
}

export interface Tab {
    key: TaskType | string;
    label: string;
    countDone: number;
    countTotal: number;
}

interface SectionView {
    id: string;
    name: string;
    tasks: Task[];
    countDone: number;
    countTotal: number;
    hasOverdue: boolean;
    isNotApplicable?: boolean;
}

@Component({
    selector: 'workflow-form',
    templateUrl: './workflow-form.component.html',
    styleUrls: ['./workflow-form.component.scss'],
})
export class WorkflowFormComponent implements OnInit, OnChanges, AfterViewInit {
    /** Flat list of tasks to render */
    @Input() tasks: Task[] = [];
    @Input() area: WorkflowArea;
    @Input() assigneeOptions: INameId[] = [];
    /** If your tasks have no `sectionId`, this label is used for the single section */
    @Input() defaultSectionName = 'All Tasks';

    /** Optional: if your tasks have a `.taskType` field but you want different labels for the tabs */
    @Input() typeLabels: Record<string, string> = {};

    @Output() exportExcel = new EventEmitter<(TaskState | ExportTasksFilterState)[]>();
    @Output() exportZip = new EventEmitter<WorkflowArea[]>();
    @Output() addNewTask = new EventEmitter<void>();
    @Output() addTask = new EventEmitter<{ createTaskDto: CreateTaskRequestDto }>();
    @Output() updateTask = new EventEmitter<{ taskId: string; updateTaskDto: UpdateTaskDto }>();
    @Output() deleteTask = new EventEmitter<{ taskId: string }>();
    @Output() bulkUpdateTasks = new EventEmitter<{ taskIds: string[]; updateTaskDto: UpdateTaskDto }>();
    @Output() syncWorkflow = new EventEmitter<void>();

    @Input() annotationsMap: { [taskId: string]: any[] } = {};
    @Input() documentsMap: { [taskId: string]: any[] } = {};
    @Input() documentsInProgressUpload: { [taskId: string]: string[] } = {};
    @Input() currentUserId: string;
    @Input() deletedUsers: GetUserDto[] = [];
    @Input() projectId: string;
    @Input() documentExpirationDate: Date;
    @Output() annotationAdded = new EventEmitter<{ taskId: string; annotation: any }>();
    @Output() annotationChanged = new EventEmitter<{ annotationId: string; annotation: any }>();
    @Output() annotationRemoved = new EventEmitter<string>();
    @Output() documentsAdded = new EventEmitter<DocumentDto[]>();
    @Output() documentRemoved = new EventEmitter<DocumentDto>();
    @Output() documentsAddedInProgress = new EventEmitter<{ taskId: string; fileNames: string[] }>();
    @Output() openFile = new EventEmitter<string>();
    @Output() downloadFile = new EventEmitter<{ fileId: string; documentName: string }>();
    @Output() stopPolling = new EventEmitter<string>();
    @Output() startPolling = new EventEmitter<string>();

    @Input() selectAll: Set<string> = new Set<string>();
    @Input() sectionToggled: Set<string> = new Set<string>();
    @Input() selectedTaskIds: Set<string> = new Set<string>();
    @Output() toggleSelectAll = new EventEmitter<string>();
    @Output() toggleSelectSection = new EventEmitter<string>();
    @Output() toggleSelectTask = new EventEmitter<TaskMetaInformation>();
    @Output() selectedTabChanged = new EventEmitter<Tab>();
    @ViewChildren('accordionItem', { read: ElementRef }) accordionItems: QueryList<ElementRef>;

    tabs: Tab[] = [];
    selectedTabKey: TaskType | string | null = null;
    selectedTabKeyLoaded = false; // Flag to indicate NgRx value has loaded
    sections: SectionView[] = [];

    /**
     * Map to track open/close state of each accordion section by section ID
     */
    openAccordions: { [sectionId: string]: boolean } = {};
    locationId: string;
    openAccordions$: Observable<{ [sectionId: string]: boolean }>;
    selectedTabKey$: Observable<string | null>;
    scrollToText$: Observable<string | null>;

    private readonly DEFAULT_FLYOUT_HEIGHT = 200; // Default estimate
    private readonly DEFAULT_FLYOUT_WIDTH = 200; // Default estimate
    private readonly VIEWPORT_PADDING = 10; // Padding from viewport edges

    flyoutPosition: {
        top: number;
        left: number;
        right: number;
        isAbove?: boolean;
    } = { top: 0, left: 0, right: 0, isAbove: false };

    openedStatusFlyoutSectionId: string | null = null;
    openedAssigneeFlyoutSectionId: string | null = null;
    openedDueDateFlyoutSectionId: string | null = null;
    openedOptionsMenuSectionId: string | null = null;
    highlightedSectionId: string | null = null;
    highlightedSectionBackgroundColor: string | null = null;

    private highlightTimeoutId: ReturnType<typeof setTimeout> | null = null;

    constructor(private store: Store, private localizedTexts: I18NextService) {
        // locationId will be set in ngOnInit or ngOnChanges
    }

    ngOnInit() {
        this.locationId = this.buildLocationId();
        this.selectedTabKey$ = this.store.pipe(select(selectSelectedTabState(this.locationId)));
        this.selectedTabKey$.subscribe((tabKey) => {
            this.selectedTabKeyLoaded = true;
            if (tabKey && tabKey !== this.selectedTabKey) {
                this.selectedTabKey = tabKey;
                this.locationId = this.buildLocationId(); // Rebuild locationId
                this.openAccordions$ = this.store.pipe(select(selectAccordionOpenState(this.locationId)));
                this.buildSections();
            } else if (!tabKey && this.tabs.length > 0) {
                this.selectTab(this.tabs[0]);
            }
        });
        this.scrollToText$ = this.store.pipe(select(selectScrollToTextState()));
    }

    ngAfterViewInit(): void {
        this.scrollToText$
            .pipe(
                delay(200), // Delay to allow for view rendering
            )
            .subscribe((text) => {
                if (text) {
                    const sectionIndex = this.sections.findIndex((section) => section.name === text);

                    if (sectionIndex > -1) {
                        const section = this.sections[sectionIndex];
                        const itemElement = this.accordionItems.toArray()[sectionIndex];

                        if (itemElement) {
                            // Ensure the accordion is open before scrolling

                            this.toggleAccordion(section.id, true);

                            // Short delay to make sure teh accordion is open before we scroll
                            setTimeout(() => {
                                // Scroll the item into view
                                itemElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

                                this.highlightSection(section.id);

                                // Dispatch action to clear the scroll-to-text state
                                this.store.dispatch(clearScrollToText());
                            }, 300);
                        }
                    }
                }
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['projectId']) {
            this.locationId = this.buildLocationId();
            this.selectedTabKey$ = this.store.pipe(select(selectSelectedTabState(this.locationId)));
            this.openAccordions$ = this.store.pipe(select(selectAccordionOpenState(this.locationId)));
        }
        if (changes['area']) {
            this.locationId = this.buildLocationId();
            this.selectedTabKey$ = this.store.pipe(select(selectSelectedTabState(this.locationId)));
            this.openAccordions$ = this.store.pipe(select(selectAccordionOpenState(this.locationId)));
        }
        if (changes['tasks']) {
            this.buildTabs();
            this.buildSections();
            if (this.openAccordions$) {
                this.openAccordions$
                    .pipe(take(1)) // take(1) to get the current value and auto-unsubscribe
                    .subscribe((openState) => {
                        if (openState) {
                            this.openAccordions = openState as { [sectionId: string]: boolean };
                        }
                    });
            }
        }
    }

    buildLocationId(): string {
        return `${this.projectId || 'unknown'}__${this.area || 'unknown'}`;
    }

    private buildTabs() {
        const tasksWithType = this.tasks.filter((t) => t.taskType != null);
        if (tasksWithType.length === 0) {
            this.tabs = [];
            this.selectedTabKey = null;
            return;
        }

        // Group tasks by taskType
        const tasksByType: Record<string, Task[]> = {};
        for (const t of tasksWithType) {
            const key = t.taskType as string;
            if (!tasksByType[key]) tasksByType[key] = [];
            tasksByType[key].push(t);
        }

        this.tabs = Object.entries(tasksByType).map(([key, tasks]) => {
            const sectionsMap = new Map<string, Task[]>();
            for (const task of tasks) {
                const sectionId = task['sectionId'] || task.sectionName || 'all';
                if (!sectionsMap.has(sectionId)) {
                    sectionsMap.set(sectionId, []);
                }
                sectionsMap.get(sectionId)!.push(task);
            }

            // Filter out N/A sections
            const sections = Array.from(sectionsMap.values())
                .map((sectionTasks) => ({
                    tasks: sectionTasks,
                    isNotApplicable: sectionTasks.every((t) => t.taskState === TaskState.NOT_APPLICABLE),
                    isDone: sectionTasks.every((t) => t.taskState === TaskState.DONE),
                }))
                .filter((section) => !section.isNotApplicable);

            const countTotal = sections.length;
            const countDone = sections.filter((section) => section.isDone).length;

            const translationKeyMap: { [key: string]: string } = {
                GENERAL: 'aumico_cp_general',
                BALANCE_SHEET: 'aumico_cp_balance_sheet',
                INCOME_STATEMENT: 'aumico_cp_income_statement',
            };
            const translationKey = translationKeyMap[key];

            // Use the specific translation if available, otherwise use the existing fallback logic.
            const label = translationKey
                ? (this.localizedTexts.t(translationKey) as string)
                : this.typeLabels[key] || this.humanize(key);

            return {
                key,
                label,
                countDone: countDone,
                countTotal: countTotal,
            };
        });

        // If no tab is selected yet, and we have tabs, select the first one.
        if (!this.selectedTabKey && this.tabs.length > 0) {
            // If the state has loaded and there was no specific tab, we default to the first.
            if (this.selectedTabKeyLoaded) {
                this.selectTab(this.tabs[0]);
            }
        }
    }

    selectTab(tab: Tab) {
        this.selectedTabKey = tab.key;
        this.locationId = this.buildLocationId(); // Rebuild locationId
        this.store.dispatch(setSelectedTabState({ locationId: this.locationId, selectedTabKey: String(tab.key) }));
        this.openAccordions$ = this.store.pipe(select(selectAccordionOpenState(this.locationId)));
        this.openAccordions$.pipe(take(1)).subscribe((openState) => {
            this.openAccordions = (openState as { [sectionId: string]: boolean }) || {};
        });
        this.buildSections();
        this.selectedTabChanged.emit(tab);
    }

    private buildSections() {
        const tasksForCurrentTab = this.tasks.filter((t) => !this.selectedTabKey || t.taskType === this.selectedTabKey);

        // Use sectionId if present, else fallback to sectionName, else 'all'
        const tasksWithSection = tasksForCurrentTab.filter((t) => t['sectionId'] || t.sectionName);

        if (tasksWithSection.length === 0) {
            this.sections = [
                {
                    id: 'all',
                    name: this.defaultSectionName,
                    tasks: tasksForCurrentTab,
                    countDone: tasksForCurrentTab.filter((t) => t.taskState === TaskState.DONE).length,
                    countTotal: tasksForCurrentTab.filter((t) => t.taskState > TaskState.NOT_APPLICABLE).length,
                    hasOverdue: tasksForCurrentTab.some(
                        (t) => t.dueDate && t.dueDate < new Date() && t.taskState !== TaskState.DONE,
                    ),
                },
            ];
            return;
        }

        const sectionsMap = new Map<string, { tasks: Task[]; name: string }>();
        for (const task of tasksForCurrentTab) {
            const sectionId = task['sectionId'] || task.sectionName || 'all';
            const sectionName = task.sectionName || sectionId;
            if (!sectionsMap.has(sectionId)) {
                sectionsMap.set(sectionId, { tasks: [], name: sectionName });
            }
            sectionsMap.get(sectionId)!.tasks.push(task);
        }

        this.sections = Array.from(sectionsMap.entries()).map(([id, { tasks, name }]) => ({
            id,
            name,
            tasks,
            countDone: tasks.filter((t) => t.taskState === TaskState.DONE).length,
            countTotal: tasks.filter((t) => t.taskState > TaskState.NOT_APPLICABLE).length,
            hasOverdue: tasks.some((t) => t.dueDate && t.dueDate < new Date() && t.taskState !== TaskState.DONE),
            isNotApplicable: tasks.every((t) => t.taskState === TaskState.NOT_APPLICABLE),
        }));
    }

    private humanize(str: string): string {
        if (!str) return '';
        // Fall-back label: "INCOME_STATEMENT" → "Income statement"
        return str
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    addTaskEvent(event: { createTaskDto: CreateTaskRequestDto }) {
        this.addTask.emit(event);
        this.buildTabs();
        this.buildSections();
    }

    updateTaskEvent(event: { taskId: string; updateTaskDto: UpdateTaskDto }) {
        this.updateTask.emit(event);
        this.buildTabs();
        this.buildSections();
    }

    deleteTaskEvent(event: { taskId: string }) {
        this.deleteTask.emit(event);
        this.buildTabs();
        this.buildSections();
    }

    createNewTask(event: { taskId: string; updateTaskDto: UpdateTaskDto }) {
        this.addNewTask.emit();
    }

    /**
     * Toggle open/close state for a section accordion
     */
    toggleAccordion(sectionId: string, enforce?: boolean) {
        const isOpen = !this.openAccordions[sectionId] || enforce;
        this.openAccordions[sectionId] = isOpen;
        this.store.dispatch(setAccordionOpenState({ locationId: this.locationId, sectionId, isOpen }));
    }

    /**
     * trackBy function for ngFor to prevent unnecessary DOM destruction
     */
    trackBySectionId(index: number, section: SectionView) {
        return section.id;
    }

    private highlightSection(sectionId: string): void {
        this.highlightedSectionId = sectionId;

        if (this.highlightTimeoutId) {
            clearTimeout(this.highlightTimeoutId);
        }

        this.highlightTimeoutId = setTimeout(() => {
            if (this.highlightedSectionId === sectionId) {
                this.highlightedSectionId = null;
                this.highlightedSectionBackgroundColor = null;
            }
        }, 900);
    }

    public onDocumentRemoved(document: DocumentDto) {
        this.documentRemoved.emit(document);
    }

    public onDocumentsAdded(documents: DocumentDto[]) {
        this.documentsAdded.emit(documents);
    }

    public onDocumentsAddedInProgress(event: { taskId: string; fileNames: string[] }) {
        this.documentsAddedInProgress.emit(event);
    }

    public exportTasksExcel(tasksFilter: (TaskState | ExportTasksFilterState)[]): void {
        this.exportExcel.emit(tasksFilter);
    }

    public exportZipClicked(workFlowAreas: WorkflowArea[]): void {
        this.exportZip.emit(workFlowAreas);
    }

    toggleOptionsMenu(event: MouseEvent, sectionId: string, buttonElementRef?: ElementRef): void {
        event.stopPropagation();

        if (buttonElementRef) {
            const buttonElement = (buttonElementRef as any).nativeElement || buttonElementRef;
            const rect = buttonElement.getBoundingClientRect();
            const position = this.calculateFlyoutPosition(rect, undefined, 222, 134); // Options menu dimensions

            this.flyoutPosition = {
                top: position.top,
                left: position.left,
                right: position.right,
                isAbove: position.isAbove,
            };
        }

        if (this.openedOptionsMenuSectionId === sectionId) {
            this.openedOptionsMenuSectionId = null;
        } else {
            this.openedOptionsMenuSectionId = sectionId;

            // Reposition after render with actual dimensions if buttonElementRef is available
            if (buttonElementRef) {
                const buttonElement = (buttonElementRef as any).nativeElement || buttonElementRef;
                const rect = buttonElement.getBoundingClientRect();
                this.repositionFlyoutAfterRender('.options-menu-flyout', rect);
            }
        }

        // Close other flyouts
        this.openedStatusFlyoutSectionId = null;
        this.openedAssigneeFlyoutSectionId = null;
        this.openedDueDateFlyoutSectionId = null;
    }

    private calculateFlyoutPosition(
        buttonRect: DOMRect,
        flyoutElement?: HTMLElement,
        flyoutWidth: number = this.DEFAULT_FLYOUT_WIDTH,
        flyoutHeight: number = this.DEFAULT_FLYOUT_HEIGHT,
    ): { top: number; left: number; right: number; isAbove: boolean } {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Use actual flyout dimensions if available, otherwise use defaults
        const actualWidth = flyoutElement ? flyoutElement.offsetWidth : flyoutWidth;
        const actualHeight = flyoutElement ? flyoutElement.offsetHeight : flyoutHeight;

        // Calculate if flyout fits below the button
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        const fitsBelow = spaceBelow >= actualHeight + this.VIEWPORT_PADDING;
        const fitsAbove = spaceAbove >= actualHeight + this.VIEWPORT_PADDING;

        // Determine vertical position
        let top: number;
        let isAbove = false;

        if (fitsBelow) {
            // Position below button
            top = buttonRect.bottom;
        } else if (fitsAbove) {
            // Position above button
            top = buttonRect.top - actualHeight;
            isAbove = true;
        } else {
            // If it doesn't fit either way, position where there's more space
            if (spaceBelow > spaceAbove) {
                top = buttonRect.bottom;
            } else {
                top = buttonRect.top - actualHeight;
                isAbove = true;
            }
        }
        // Calculate horizontal position
        let left = buttonRect.left;
        let right = buttonRect.right;

        // Check if flyout extends beyond right edge of viewport
        if (buttonRect.left + actualWidth > viewportWidth - this.VIEWPORT_PADDING) {
            // Align flyout's right edge with button's right edge
            left = buttonRect.right - actualWidth;
            right = buttonRect.right;
        }

        // Ensure flyout doesn't extend beyond left edge
        if (left < this.VIEWPORT_PADDING) {
            left = this.VIEWPORT_PADDING;
            right = left + actualWidth;
        }

        return { top, left, right, isAbove };
    }

    private repositionFlyoutAfterRender(flyoutSelector: string, buttonRect: DOMRect): void {
        setTimeout(() => {
            const flyoutElement = document.querySelector(flyoutSelector) as HTMLElement;
            if (flyoutElement) {
                const position = this.calculateFlyoutPosition(buttonRect, flyoutElement);
                this.flyoutPosition = {
                    top: position.top,
                    left: position.left,
                    right: position.right,
                    isAbove: position.isAbove,
                };
            }
        }, 0);
    }

    closeOptionsMenu(): void {
        this.openedOptionsMenuSectionId = null;
    }

    onMarkSectionNotApplicable(secId: string) {
        const section = this.sections.find((s) => s.id === secId);
        if (!section) return;

        this.openedOptionsMenuSectionId = null; // Close the options menu after marking
        const taskIds = section.tasks.map((task) => task['id']);
        this.bulkUpdateTasks.emit({ taskIds, updateTaskDto: { taskState: TaskState.NOT_APPLICABLE } });
    }

    onMarkSectionApplicable(secId: string) {
        const section = this.sections.find((s) => s.id === secId);
        if (!section) return;

        this.openedOptionsMenuSectionId = null; // Close the options menu after marking

        // Bulk update tasks to applicable state
        const taskIds = section.tasks.map((task) => task['id']);
        this.bulkUpdateTasks.emit({ taskIds, updateTaskDto: { taskState: TaskState.OPEN } });
    }

    onSyncWorkflow() {
        this.syncWorkflow.emit();
    }

    onOpenFile(fileId: string): void {
        this.openFile.emit(fileId);
    }

    onDownloadFile(fileId: string, documentName: string): void {
        this.downloadFile.emit({ fileId, documentName });
    }

    onStartPolling(fileId: string): void {
        this.startPolling.emit(fileId);
    }

    onStopPolling(fileId: string): void {
        this.stopPolling.emit(fileId);
    }
    // Button at the top of the page to select all tasks
    toggleCheckAll() {
        // Just emit event, let parent handle the logic
        this.toggleSelectAll.emit(this.selectedTabKey);
    }

    toggleSectionSelectAll(sec) {
        // Just emit event, let parent handle the logic
        this.toggleSelectSection.emit(sec);
    }

    toggleCheckTask(task: TaskMetaInformation) {
        // Just emit event, let parent handle the logic
        this.toggleSelectTask.emit(task);
    }
}
